import {
  CButton,
  CButtonGroup,
  CCol,
  CContainer,
  CDataTable,
  CFormGroup,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CRow,
} from "@coreui/react";
import { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { getList } from "../../api/generics";
import { SUCCESS } from "../../utils/constants/tags";
import { RootState } from "../../store";
import { useSelector } from "react-redux";
import { formatDateTime } from "../../utils/dates";
import { defaultValueOnUndefined } from "../../utils/fields";
import Discount, { DiscountLog } from "../../models/discount";

interface DiscountHistoryModalProps {
  show: boolean;
  discount?: Discount;
  onCancel: () => void | Promise<void>;
}

const DiscountHistoryModal: React.FC<DiscountHistoryModalProps> = ({
  show,
  discount,
  onCancel,
}) => {
  const fields = [
    {
      key: "createdAt",
      _classes: ["text-center"],
      label: "Fecha",
    },
    {
      key: "status",
      _classes: ["text-center"],
      label: "Estado",
    },
    {
      key: "storesAffected",
      _classes: ["text-center"],
      label: "Sucursales Afectadas",
    },
    {
      key: "categoriesAffected",
      _classes: ["text-center"],
      label: "Categorias Afectadas",
    },
    {
      key: "productsAffected",
      _classes: ["text-center"],
      label: "Códigos de Productos Afectados",
    },
    {
      key: "userName",
      _classes: ["text-center"],
      label: "Usuario",
    },
  ];

  const [logItems, setLogItems] = useState<DiscountLog[]>([]);
  const [loading, setLoading] = useState(false);
  const company = useSelector((state: RootState) => state.company.data.company);

  const getLogItems = async () => {
    if (discount === undefined || discount.id === undefined) {
      setLogItems([]);
      return;
    }
    const discountIdStr = discount.id.toString();
    if (discountIdStr === undefined) {
      setLogItems([]);
      return;
    }
    const limit = 2000;
    const offset = 0;
    let additionalParams: Map<string, string> | undefined = new Map<
      string,
      string
    >();
    additionalParams.set("company_id", company.id!.toString());
    additionalParams.set("discount_id", discountIdStr);
    setLoading(true);
    const logItemsStatus = await getList<DiscountLog>(
      `/discounts/changes_logs/`,
      limit,
      offset,
      additionalParams
    );
    if (logItemsStatus.status === SUCCESS) {
      if (logItemsStatus.data !== undefined) {
        setLogItems(logItemsStatus.data.items);
      } else {
        setLogItems([]);
        setLoading(false);
        return;
      }
    } else {
      setLogItems([]);
      setLoading(false);
      return;
    }
    setLoading(false);
  };

  const onClose = () => {
    clearForm();
    onCancel();
  };

  const clearForm = () => {
    setLogItems([]);
  };

  useEffect(() => {
    getLogItems();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discount]);

  if (loading) {
    return (
      <Spinner
        animation="grow"
        style={{
          height: "17px",
          width: "17px",
          marginTop: "auto",
          marginBottom: "auto",
          marginRight: "10px",
        }}
      />
    );
  }

  return (
    <CModal show={show} className="modal-info" onClosed={onCancel} size="xl">
      <CModalHeader closeButton>
        <CModalTitle>Historial de Descuento</CModalTitle>
      </CModalHeader>
      <CModalBody>
        <CContainer fluid>
          <CRow>
            <CCol sm="12">
              <div>
                <CDataTable
                  noItemsView={<h2 className="text-center">Sin Resultados</h2>}
                  addTableClasses={"table-fixed"}
                  fields={fields}
                  items={logItems}
                  striped
                  border
                  loading={loading}
                  responsive
                  scopedSlots={{
                    status: (item: DiscountLog) => {
                      return (
                        <td className="text-center">
                          {defaultValueOnUndefined("-", item.status)}
                        </td>
                      );
                    },
                    storesAffected: (item: DiscountLog) => {
                      return (
                        <td className="text-center">
                          {defaultValueOnUndefined("-", item.storesAffected)}
                        </td>
                      );
                    },
                    categoriesAffected: (item: DiscountLog) => {
                      return (
                        <td className="text-center">
                          {defaultValueOnUndefined(
                            "-",
                            item.categoriesAffected
                          )}
                        </td>
                      );
                    },
                    productsAffected: (item: DiscountLog) => {
                      return (
                        <td className="text-center">
                          {defaultValueOnUndefined("-", item.productsAffected)}
                        </td>
                      );
                    },
                    userName: (item: DiscountLog) => {
                      return (
                        <td className="text-center">
                          {defaultValueOnUndefined("-", item.userName)}
                        </td>
                      );
                    },
                    createdAt: (item: DiscountLog) => {
                      return (
                        <td className="text-center">
                          {item.createdAt
                            ? formatDateTime(new Date(item.createdAt))
                            : "-"}
                        </td>
                      );
                    },
                  }}
                />
              </div>
            </CCol>
          </CRow>
        </CContainer>
      </CModalBody>
      <CModalFooter>
        <CFormGroup className="float-right">
          <CButtonGroup>
            <CButton type="button" color="secondary" onClick={onClose}>
              Atras
            </CButton>
          </CButtonGroup>
        </CFormGroup>
      </CModalFooter>
    </CModal>
  );
};

export default DiscountHistoryModal;
